import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useApi } from "@/shared/utils/request";
import { logger } from "@/shared/utils/logger";
import { getFirmDomain } from "@/shared/utils/firm";
import EntriesTable, { ColumnWidths, SortingMode } from "../components/EntriesTable/EntriesTable";
import EditEntryDialog from "../components/EditDialog";
import { MatterPickerDialog } from "../components/MatterPickerDialog";
import { ExportValidationDialog, InvalidMatter } from "../components/ExportValidationDialog";
import toast from "react-hot-toast";
import { useDashboardData, Entry } from "@/shared/contexts/DashboardDataContext";

type Props = {
  date: Date;
  dateRange?: { from: Date | undefined; to: Date | undefined };
  sortingMode: SortingMode;
  onSelectionChange?: (count: number) => void;
  onToggleSortingMode?: () => void;
  filterEntries?: <T extends { application?: string }>(entries: T[]) => T[];
};
export type TrackedViewHandle = {
  combineSelected: () => Promise<void>;
  deleteSelected: () => Promise<void>;
  exportSelected: () => Promise<void>;
  duplicateSelected: () => Promise<void>;
};

const TrackedView = forwardRef<TrackedViewHandle, Props>(function TrackedView({ date, dateRange, sortingMode, onSelectionChange, onToggleSortingMode, filterEntries }: Props, ref) {
  const { makeApiRequest } = useApi();
  const { entries, exportedEntries, matters, tasks, activities, refreshRecords } = useDashboardData();

  const [groupedEntries, setGroupedEntries] = useState<Record<string, Entry[]>>({});
  const [_groupedExportedEntries, setGroupedExportedEntries] = useState<Record<string, Entry[]>>({});

  const defaultColumnWidths: ColumnWidths = { date: 7, matter: 25, codes: 7, narrative: 56, units: 10 };
  const [columnWidths, setColumnWidths] = useState<ColumnWidths>(() => {
    try {
      const saved = localStorage.getItem("dashboard-column-widths");
      if (!saved) return defaultColumnWidths;
      const parsed = JSON.parse(saved);
      // Migrate legacy keys: task->codes, description->narrative
      const migrated: Partial<ColumnWidths> = { ...parsed };
      if (parsed.task && !parsed.codes) migrated.codes = parsed.task;
      if (parsed.description && !parsed.narrative) migrated.narrative = parsed.description;
      // Remove legacy keys that are no longer used
      delete (migrated as any).title;
      delete (migrated as any).description;
      delete (migrated as any).task;
      return { ...defaultColumnWidths, ...migrated } as ColumnWidths;
    } catch {
      return defaultColumnWidths;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("dashboard-column-widths", JSON.stringify(columnWidths));
    } catch {}
  }, [columnWidths]);

  const columnMinWidths: ColumnWidths = { date: 5, matter: 8, codes: 6, narrative: 20, units: 6 };
  const columnOrder: (keyof ColumnWidths)[] = ["date", "matter", "codes", "narrative"];

  const handleCascadingResize = (resizingColumn: keyof ColumnWidths, deltaPercentage: number) => {
    const currentIndex = columnOrder.indexOf(resizingColumn);
    if (currentIndex === -1) return;
    const newWidths = { ...columnWidths };
    const currentWidth = columnWidths[resizingColumn];
    const minWidth = columnMinWidths[resizingColumn];
    if (deltaPercentage > 0) {
      let availableSpace = 0;
      for (let i = currentIndex + 1; i < columnOrder.length; i++) {
        const key = columnOrder[i];
        const colWidth = columnWidths[key];
        const colMin = columnMinWidths[key];
        availableSpace += Math.max(0, colWidth - colMin);
      }
      const actualExpansion = Math.min(deltaPercentage, availableSpace);
      if (actualExpansion <= 0) return;
      newWidths[resizingColumn] = currentWidth + actualExpansion;
      let remaining = actualExpansion;
      for (let i = currentIndex + 1; i < columnOrder.length && remaining > 0.01; i++) {
        const key = columnOrder[i];
        const colWidth = columnWidths[key];
        const colMin = columnMinWidths[key];
        const canRemove = Math.max(0, colWidth - colMin);
        const toRemove = Math.min(remaining, canRemove);
        if (toRemove > 0.01) {
          newWidths[key] = colWidth - toRemove;
          remaining -= toRemove;
        }
      }
    } else {
      const maxShrinkage = currentWidth - minWidth;
      const actualShrinkage = Math.max(deltaPercentage, -maxShrinkage);
      if (actualShrinkage >= 0) return;
      newWidths[resizingColumn] = currentWidth + actualShrinkage;
      const spaceToGive = -actualShrinkage;
      for (let i = currentIndex + 1; i < columnOrder.length; i++) {
        const key = columnOrder[i];
        newWidths[key] = columnWidths[key] + spaceToGive;
        break;
      }
    }
    setColumnWidths(newWidths);
  };

  function groupEntriesByHour(rows: Entry[]): Record<string, Entry[]> {
    const groups: Record<string, Entry[]> = {};
    rows.forEach((row) => {
      let hourLabel = "";
      if (row.end_time) {
        const dateObj = new Date(row.end_time);
        const hours = dateObj.getUTCHours();
        const period = hours >= 12 ? "PM" : "AM";
        const displayHour = hours % 12 || 12;
        hourLabel = `${displayHour}:00 ${period}`;
      }
      const key = hourLabel || "Unknown";
      if (!groups[key]) groups[key] = [];
      groups[key].push(row);
    });
    return groups;
  }

  function groupEntriesByMatter(rows: Entry[]): Record<string, Entry[]> {
    const unsorted: Record<string, Entry[]> = {};
    rows.forEach((row) => {
      const key = row.matter || "Unknown";
      if (!unsorted[key]) unsorted[key] = [];
      unsorted[key].push(row);
    });
    // Sort group keys alphabetically so order is stable across re-fetches
    const sorted: Record<string, Entry[]> = {};
    Object.keys(unsorted).sort((a, b) => a.localeCompare(b)).forEach((key) => {
      sorted[key] = unsorted[key];
    });
    return sorted;
  }

  // Filter by single day or date range
  const isInRange = (d: Date) => {
    if (dateRange?.from || dateRange?.to) {
      const start = dateRange.from ? new Date(dateRange.from.getFullYear(), dateRange.from.getMonth(), dateRange.from.getDate(), 0, 0, 0, 0) : null;
      const end = dateRange.to ? new Date(dateRange.to.getFullYear(), dateRange.to.getMonth(), dateRange.to.getDate(), 23, 59, 59, 999) : null;
      if (start && end) return d >= start && d <= end;
      if (start) return d >= start;
      if (end) return d <= end;
      return true;
    }
    const sameDay = d.getFullYear() === date.getFullYear() && d.getMonth() === date.getMonth() && d.getDate() === date.getDate();
    return sameDay;
  };

  const filteredEntries = useMemo(() => {
    // First filter by date
    const dateFiltered = entries.filter((e) => {
      const d = new Date(e.date);
      return isInRange(d);
    });
    // Then apply application filter if provided
    return filterEntries ? filterEntries(dateFiltered) : dateFiltered;
  }, [entries, date, dateRange, filterEntries]);

  const filteredExported = useMemo(() => {
    return exportedEntries.filter((e) => {
      const d = new Date(e.date);
      return isInRange(d);
    });
  }, [exportedEntries, date, dateRange]);

  useEffect(() => {
    if (sortingMode === "matter") {
      setGroupedEntries(groupEntriesByMatter(filteredEntries));
      setGroupedExportedEntries(groupEntriesByMatter(filteredExported));
    } else {
      setGroupedEntries(groupEntriesByHour(filteredEntries));
      setGroupedExportedEntries(groupEntriesByHour(filteredExported));
    }
  }, [filteredEntries, filteredExported, sortingMode]);

  // Selection state
  const [checkedRows, setCheckedRows] = useState<Set<number>>(new Set());
  const relevantEntriesCount = filteredEntries.length;
  const selectAllChecked = checkedRows.size > 0 && checkedRows.size === relevantEntriesCount;
  const selectAllIndeterminate = checkedRows.size > 0 && checkedRows.size < relevantEntriesCount;

  const onToggleSelectAll = () => {
    if (selectAllChecked || selectAllIndeterminate) {
      setCheckedRows(new Set());
    } else {
      setCheckedRows(new Set(filteredEntries.map((e) => e.id)));
    }
  };

  const onToggleRow = (id: number) => {
    setCheckedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    onSelectionChange?.(checkedRows.size);
  }, [checkedRows, onSelectionChange]);

  const performCombine = async (ids: number[], matter_id: string) => {
    try {
      await logger.trackOperation(
        'Combine entries',
        async () => {
          const resp = await makeApiRequest({
            endpoint: "/records/combine",
            method: "POST",
            body: { ids, end_time: new Date().toLocaleTimeString(), matter_id }
          });
          if (resp.error) throw new Error(String(resp.error));
          await refreshRecords();
        },
        { entryCount: ids.length, matterId: matter_id }
      );
      setCheckedRows(new Set());
      toast.success("Combined entries");
    } catch (e) {
      if (e instanceof Error) {
        logger.captureException(e, { action: 'combine', entryCount: ids.length });
      }
      toast.error("Error combining entries");
    }
  };

  const combineSelected = async () => {
    if (checkedRows.size === 0) {
      logger.warn("Combine validation failed - no entries selected", {
        action: 'combine',
        checkedRowsSize: checkedRows.size,
        filteredEntriesCount: filteredEntries.length,
        totalEntriesCount: entries.length,
      });
      toast.error("Please select entries to combine");
      return;
    }

    const selected = filteredEntries.filter(e => checkedRows.has(e.id));
    const ids = selected.map(e => e.id);

    // Extract matter_ids from selected entries
    const matterIds = selected.map(e => e.matter_id).filter(Boolean) as string[];
    const uniqueMatterIds = Array.from(new Set(matterIds));

    // Case 1: All same matter OR Case 2: One has matter, rest are blank
    if (uniqueMatterIds.length === 1) {
      await performCombine(ids, uniqueMatterIds[0]);
    }
    // Case 3: Multiple different matters - prompt user with conflict warning
    else if (uniqueMatterIds.length > 1) {
      setPendingCombineIds(ids);
      setConflictingMatterIds(uniqueMatterIds);
      setMatterDialogOpen(true);
    }
    // Case 4: None have matters - prompt user to select
    else {
      setPendingCombineIds(ids);
      setConflictingMatterIds([]);
      setMatterDialogOpen(true);
    }
  };

  const performExport = async (ids: number[]) => {
    try {
      let exportedCount = 0;
      await logger.trackOperation(
        'Export entries',
        async () => {
          const resp = await makeApiRequest({ endpoint: "/records/exportRecords", method: "POST", body: { ids } });
          if (resp.error) throw new Error(String(resp.error));
          exportedCount = resp.data?.length ?? 0;
          await refreshRecords();
        },
        { entryCount: ids.length }
      );
      setCheckedRows(new Set());

      // Check if all entries were exported
      if (exportedCount === 0) {
        toast.error("No entries were exported. Please ensure entries are complete before exporting.");
      } else if (exportedCount < ids.length) {
        toast.success(`Exported ${exportedCount} of ${ids.length} entries. Some incomplete entries were skipped.`);
      } else {
        toast.success("Exported entries");
      }
    } catch (e) {
      if (e instanceof Error) {
        logger.captureException(e, { action: 'export', entryCount: ids.length });
        toast.error(e.message || "Error exporting entries");
      } else {
        toast.error("Error exporting entries");
      }
    }
  };

  const exportSelected = async () => {
    if (checkedRows.size === 0) {
      logger.warn("Export validation failed - no entries selected", {
        action: 'export',
        checkedRowsSize: checkedRows.size,
        filteredEntriesCount: filteredEntries.length,
        totalEntriesCount: entries.length,
      });
      toast.error("Please select entries to export");
      return;
    }
    const ids = filteredEntries.filter(e => checkedRows.has(e.id)).map(e => e.id);

    // Validate export - check for matters not assigned to this professional
    // Skip for Tyson Mendes until validation endpoint is fixed for their setup
    if (getFirmDomain() !== "tysonmendes") {
      try {
        const resp = await makeApiRequest({ endpoint: "/records/validateExport", method: "POST", body: { ids } });
        if (resp.data?.invalidMatters?.length > 0) {
          setPendingExportIds(ids);
          setInvalidMatters(resp.data.invalidMatters);
          setExportValidationOpen(true);
          return;
        }
      } catch {
        // Validation failed silently - proceed with export
      }
    }

    await performExport(ids);
  };

  const deleteSelected = async () => {
    if (checkedRows.size === 0) {
      logger.warn("Delete validation failed - no entries selected", {
        action: 'delete',
        checkedRowsSize: checkedRows.size,
        filteredEntriesCount: filteredEntries.length,
        totalEntriesCount: entries.length,
      });
      toast.error("Please select entries to delete");
      return;
    }
    const ids = filteredEntries.filter(e => checkedRows.has(e.id)).map(e => e.id);
    try {
      const resp = await makeApiRequest({ endpoint: "/records/delete", method: "POST", body: { ids } });
      if (resp.error) throw new Error(String(resp.error));
      await refreshRecords();
      setCheckedRows(new Set());
      toast.success(`Deleted ${ids.length} ${ids.length === 1 ? 'entry' : 'entries'}`);
    } catch (e) {
      if (e instanceof Error) {
        logger.captureException(e, { action: 'delete', entryCount: ids.length });
      }
      toast.error("Error deleting entries");
    }
  };

  const duplicateSelected = async () => {
    if (checkedRows.size === 0) {
      logger.warn("Duplicate validation failed - no entries selected", {
        action: 'duplicate',
        checkedRowsSize: checkedRows.size,
        filteredEntriesCount: filteredEntries.length,
        totalEntriesCount: entries.length,
      });
      toast.error("Please select entries to duplicate");
      return;
    }
    const ids = filteredEntries.filter(e => checkedRows.has(e.id)).map(e => e.id);
    try {
      const resp = await makeApiRequest({ endpoint: "/records/duplicate", method: "POST", body: { ids } });
      if (resp.error) throw new Error(String(resp.error));
      await refreshRecords();
      setCheckedRows(new Set());
      toast.success(`Duplicated ${ids.length} ${ids.length === 1 ? 'entry' : 'entries'}`);
    } catch (e) {
      if (e instanceof Error) {
        logger.captureException(e, { action: 'duplicate', entryCount: ids.length });
      }
      toast.error("Error duplicating entries");
    }
  };

  // Duplicate a single entry from the edit dialog (with current form values)
  const handleDuplicateEntry = async (entry: {
    date: string;
    matter: string;
    matter_id: string;
    description: string;
    duration: number;
    task: string;
    activity: string;
    document_name: string | null;
    client: string | null;
    units_seconds: number;
    start_time: string | null;
    end_time: string | null;
  }, originalId: number) => {
    const resp = await makeApiRequest({
      endpoint: "/records/duplicate",
      method: "POST",
      body: {
        entry: {
          date: entry.date,
          matter_id: entry.matter_id,
          description: entry.description,
          units: entry.duration,
          task_code: entry.task?.trim() || null,
          activity_code: entry.activity?.trim() || null,
          document_name: entry.document_name?.trim() || null,
          client: entry.client?.trim() || null,
          units_seconds: entry.units_seconds || 0,
          start_time: entry.start_time || null,
          end_time: entry.end_time || null,
          duplicated_from_id: originalId,
        }
      },
    });
    if (resp.error) throw new Error(String(resp.error));
    await refreshRecords();
  };

  // Edit dialog state
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any | null>(null);

  // Matter picker dialog state
  const [matterDialogOpen, setMatterDialogOpen] = useState(false);
  const [pendingCombineIds, setPendingCombineIds] = useState<number[]>([]);
  const [conflictingMatterIds, setConflictingMatterIds] = useState<string[]>([]);

  // Export validation dialog state
  const [exportValidationOpen, setExportValidationOpen] = useState(false);
  const [invalidMatters, setInvalidMatters] = useState<InvalidMatter[]>([]);
  const [pendingExportIds, setPendingExportIds] = useState<number[]>([]);

  const onRowClick = (entry: Entry) => {
    setSelectedRowData({
      id: entry.id,
      date: entry.date,
      matter_name: entry.matter,
      matter_id: entry.matter_id || "",
      description: entry.description,
      duration: entry.duration,
      task: entry.task,
      activity: entry.activity || "",
      document_name: entry.document_name || "",
      units_seconds: entry.units_seconds || 0,
      start_time: entry.start_time || "",
      end_time: entry.end_time || "",
      application: entry.application || "",
      attorney_feedback: entry.attorney_feedback || null,
    });
    setShowEditModal(true);
  };

  const handleResolveFeedback = async (entryId: number) => {
    try {
      const resp = await makeApiRequest({
        endpoint: `/records/${entryId}/resolve-feedback`,
        method: "POST",
      });
      if (resp.error) throw new Error(String(resp.error));
      await refreshRecords();
      // Update selectedRowData to clear the feedback
      setSelectedRowData((prev: any) => prev ? { ...prev, attorney_feedback: null } : null);
      toast.success("Feedback resolved");
    } catch (e) {
      if (e instanceof Error) {
        logger.captureException(e, { action: 'resolveFeedback', entryId });
      }
      toast.error("Error resolving feedback");
    }
  };

  const onSaveEditEntry = async (
    updatedEntry: {
      id: number; date: string; matter: string; matter_id: string; description: string; duration: number; task: string; activity: string;
    },
    shouldExport: boolean
  ) => {
    try {
      // First, save the entry
      const resp = await makeApiRequest({ endpoint: "/records/edit", method: "POST", body: { ...updatedEntry } });
      if (resp.error) throw new Error(String(resp.error));
      await refreshRecords();

      // If shouldExport, validate first then export
      if (shouldExport) {
        const ids = [updatedEntry.id];

        // Validate export
        try {
          const validationResp = await makeApiRequest({ endpoint: "/records/validateExport", method: "POST", body: { ids } });
          if (validationResp.data?.invalidMatters?.length > 0) {
            // Show validation dialog - user can choose to proceed or cancel
            setPendingExportIds(ids);
            setInvalidMatters(validationResp.data.invalidMatters);
            setExportValidationOpen(true);
            setShowEditModal(false);
            return; // Don't export yet - wait for user decision
          }
        } catch {
          // Validation failed silently - proceed with export
        }

        // No invalid matters - proceed with export
        await performExport(ids);
      }
    } catch (e) {
      if (e instanceof Error) {
        logger.captureException(e, { action: 'editEntry', entryId: updatedEntry.id, shouldExport });
      }
      toast.error("Error saving entry");
    } finally {
      setShowEditModal(false);
    }
  };

  useImperativeHandle(ref, () => ({ combineSelected, deleteSelected, exportSelected, duplicateSelected }), [combineSelected, deleteSelected, exportSelected, duplicateSelected]);

  return (
    <>
      <EntriesTable
        entries={filteredEntries}
        groupedEntries={groupedEntries}
        columnWidths={columnWidths}
        onResizeColumn={(key, w) => setColumnWidths((prev) => ({ ...prev, [key]: w }))}
        onCascadingResize={handleCascadingResize}
        checkedRows={checkedRows}
        selectAllChecked={selectAllChecked}
        selectAllIndeterminate={selectAllIndeterminate}
        onToggleSelectAll={onToggleSelectAll}
        onToggleRow={onToggleRow}
        onRowClick={onRowClick}
        onToggleSortingMode={onToggleSortingMode}
        mode="entries"
        matters={matters}
        sortingMode={sortingMode}
      />
      {selectedRowData && (
        <EditEntryDialog
          onSave={onSaveEditEntry}
          onDuplicate={handleDuplicateEntry}
          matters={matters}
          tasks={tasks}
          activities={activities}
          selectedRowData={selectedRowData}
          openModal={showEditModal}
          setOpenModal={setShowEditModal}
          isInsuranceDefenseModeRestricted={false}
          checkForRedWords={false}
          attorneyFeedback={selectedRowData.attorney_feedback}
          onResolveFeedback={() => handleResolveFeedback(selectedRowData.id)}
        />
      )}
      <MatterPickerDialog
        open={matterDialogOpen}
        onOpenChange={setMatterDialogOpen}
        matters={matters}
        conflictingMatters={conflictingMatterIds}
        onSelect={(matterId) => {
          performCombine(pendingCombineIds, matterId);
          setMatterDialogOpen(false);
        }}
        onCancel={() => {
          setMatterDialogOpen(false);
          setPendingCombineIds([]);
          setConflictingMatterIds([]);
        }}
        title="Select a Matter"
        description={
          conflictingMatterIds.length > 0
            ? "The selected entries have different matters. Choose which matter to assign to the combined entry:"
            : "Choose which matter to assign to the combined entry:"
        }
      />
      <ExportValidationDialog
        open={exportValidationOpen}
        onOpenChange={setExportValidationOpen}
        invalidMatters={invalidMatters}
        onProceed={() => {
          setExportValidationOpen(false);
          performExport(pendingExportIds);
          setPendingExportIds([]);
          setInvalidMatters([]);
        }}
        onCancel={() => {
          setExportValidationOpen(false);
          setPendingExportIds([]);
          setInvalidMatters([]);
        }}
      />
    </>
  );
});

export default TrackedView;
